"use strict";

const PAGE_SELECTOR = '.product-single';
const NAV_SELECTOR = '.product-nav';
const LIST_SELECTOR = '.product-nav-list';
const HEADER_SELECTOR = '.page-header';
const LINK_SELECTOR = '.product-nav-link';
const STICKY_TOLERANCE = 1;
const SCROLL_EDGE_TOLERANCE = 2;

function getHeaderHeight(header) {
    return header?.getBoundingClientRect().height ?? 0;
}

function bindScroll(handler) {
    const lenis = window.__lenis;

    if (lenis && typeof lenis.on === 'function') {
        lenis.on('scroll', handler);
        return () => lenis.off?.('scroll', handler);
    }

    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
}

function updateScrollHints(nav, list) {
    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    const isScrollable = maxScrollLeft > SCROLL_EDGE_TOLERANCE;

    nav.classList.toggle('is-scrollable', isScrollable);
    nav.classList.toggle('can-scroll-left', isScrollable && list.scrollLeft > SCROLL_EDGE_TOLERANCE);
    nav.classList.toggle(
        'can-scroll-right',
        isScrollable && list.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE
    );
}

function scrollLinkIntoView(list, link, { smooth = true } = {}) {
    if (!list || !link) {
        return;
    }

    const listRect = list.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const isFullyVisible =
        linkRect.left >= listRect.left + SCROLL_EDGE_TOLERANCE &&
        linkRect.right <= listRect.right - SCROLL_EDGE_TOLERANCE;

    if (isFullyVisible) {
        return;
    }

    const target =
        link.offsetLeft - (list.clientWidth / 2) + (link.offsetWidth / 2);

    list.scrollTo({
        left: Math.max(0, target),
        behavior: smooth ? 'smooth' : 'auto',
    });
}

function initProductNav() {
    const nav = document.querySelector(`${PAGE_SELECTOR} ${NAV_SELECTOR}`);
    if (!nav) {
        return;
    }

    const list = nav.querySelector(LIST_SELECTOR);
    const header = document.querySelector(HEADER_SELECTOR);
    const links = Array.from(nav.querySelectorAll(LINK_SELECTOR));
    const sections = links
        .map(function (link) {
            const id = (link.getAttribute('href') || '').replace('#', '').trim();
            return id ? document.getElementById(id) : null;
        })
        .filter(Boolean);

    let ticking = false;
    let activeHref = null;

    function update() {
        const headerHeight = getHeaderHeight(header);
        const scrollOffset = headerHeight + nav.offsetHeight;

        document.documentElement.style.setProperty('--product-nav-sticky-top', `${headerHeight}px`);
        document.documentElement.style.setProperty('--product-nav-scroll-offset', `${scrollOffset}px`);

        const navTop = nav.getBoundingClientRect().top;
        nav.classList.toggle('is-sticky', navTop <= headerHeight + STICKY_TOLERANCE);

        if (list) {
            updateScrollHints(nav, list);
        }

        if (!sections.length) {
            return;
        }

        const offset = headerHeight + nav.offsetHeight;
        let currentId = null;

        sections.forEach(function (section) {
            if (section.getBoundingClientRect().top <= offset) {
                currentId = section.id;
            }
        });

        const nextHref = currentId ? `#${currentId}` : null;

        links.forEach(function (link) {
            const href = link.getAttribute('href');
            link.classList.toggle('active', href === nextHref);
        });

        if (list && nextHref && nextHref !== activeHref) {
            const activeLink = links.find((link) => link.getAttribute('href') === nextHref);
            scrollLinkIntoView(list, activeLink);
        }

        activeHref = nextHref;
    }

    function onScroll() {
        if (ticking) {
            return;
        }

        ticking = true;
        requestAnimationFrame(function () {
            update();
            ticking = false;
        });
    }

    const unbindScroll = bindScroll(onScroll);
    const observers = [];

    if (typeof ResizeObserver !== 'undefined') {
        const resizeObserver = new ResizeObserver(onScroll);

        if (header) {
            resizeObserver.observe(header);

            const topbar = header.querySelector('.header-topbar');
            const navbar = header.querySelector('.header-navbar');

            if (topbar) {
                resizeObserver.observe(topbar);
            }

            if (navbar) {
                resizeObserver.observe(navbar);
            }
        }

        if (list) {
            resizeObserver.observe(list);
        }

        observers.push(resizeObserver);
    }

    if (list) {
        list.addEventListener('scroll', function () {
            updateScrollHints(nav, list);
        }, { passive: true });

        links.forEach(function (link) {
            link.addEventListener('focus', function () {
                scrollLinkIntoView(list, link, { smooth: false });
            });
        });
    }

    window.addEventListener('resize', onScroll, { passive: true });
    update();

    return function cleanup() {
        unbindScroll?.();
        window.removeEventListener('resize', onScroll);
        observers.forEach((observer) => observer.disconnect());
    };
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductNav, { once: true });
} else {
    initProductNav();
}
